import {use, useLayoutEffect, useState} from 'react'

import {ExperimentScopeContext, ExperimentStoreContext} from './experiment-context'
import {type ExperimentScope, type ExperimentStore, unavailableExperimentScope} from './internal'
import {equivalentScopes, withAssignmentContext} from './store-validators'

/**
 * Resolves scope for the rendered destination without changing the store.
 *
 * Reuses the document scope when unchanged or equivalent. New destinations use their
 * resolved assignments with merged, validated context. Missing assignments are unavailable.
 */
const resolveRenderedScope = ({
  isNewDestination,
  documentScope,
  resolveCandidate,
  assignmentContext,
}: {
  isNewDestination: boolean
  documentScope: ExperimentScope
  resolveCandidate?: () => ExperimentScope | undefined
  assignmentContext: string
}): ExperimentScope => {
  let renderedScope = documentScope
  if (isNewDestination) {
    const candidateScope = resolveCandidate?.() ?? unavailableExperimentScope()
    const resolvedScope = withAssignmentContext(candidateScope, assignmentContext)
    renderedScope = equivalentScopes(resolvedScope, documentScope) ? documentScope : resolvedScope
  }
  return renderedScope
}

/** Destination keys are unique by pathname + search. */
type NavigationScopeTransactionOptions = {
  /** Identifies the destination whose route tree is currently rendered. */
  destinationKey: string
  /**
   * Returns the rendered destination's assignment scope, or undefined when none was provided.
   * Only called for a new rendered destination, never for a pending target.
   */
  resolveCandidate?: () => ExperimentScope | undefined
}

type NavigationScopeTransaction = {
  /** The assignment scope that descendants should use for the current render. */
  scope: ExperimentScope
}

/**
 * Keeps assignment scopes in step with navigation.
 *
 * During render, chooses the page's scope without changing the document store. A new destination
 * uses its effective assignment rather than inheriting the previous destination's assignment.
 *
 * After React commits the render, makes that destination's assignment the document scope.
 * Pending targets do not change the document scope.
 */
export function useNavigationScopeTransaction({
  destinationKey,
  resolveCandidate,
}: NavigationScopeTransactionOptions): NavigationScopeTransaction {
  const store = use(ExperimentStoreContext)
  const documentScope = use(ExperimentScopeContext)
  if (!store || !documentScope) {
    throw new Error('useNavigationScopeTransaction must be used within ExperimentDocumentProvider')
  }

  // Each hook tracks commits without triggering renders. Replacing the store starts with its current assignment.
  const [transaction, setTransaction] = useState(() => createNavigationTransaction(store, destinationKey))
  let navigation = transaction
  if (navigation.store !== store) {
    navigation = createNavigationTransaction(store, destinationKey)
    setTransaction(navigation)
  }

  const renderedScope = resolveRenderedScope({
    isNewDestination: destinationKey !== navigation.storeDestinationKey,
    documentScope,
    resolveCandidate,
    assignmentContext: store.getAssignmentContext(),
  })

  // Wait until React commits the render before changing the store, so a render React abandons
  // cannot change the document assignment.
  useLayoutEffect(() => {
    navigation.applyScope({destinationKey, renderedScope})
  }, [destinationKey, renderedScope, navigation])

  return {scope: renderedScope}
}

/**
 * Tracks the committed destination without triggering React renders.
 *
 * Each useNavigationScopeTransaction instance creates one of these objects for its current store.
 * It reads the committed destination key during render and calls applyScope after React commits.
 * Abandoned renders leave this state unchanged.
 */
function createNavigationTransaction(store: ExperimentStore, initialDestinationKey: string) {
  let storeDestinationKey = initialDestinationKey

  const applyScope = ({destinationKey, renderedScope}: {destinationKey: string; renderedScope: ExperimentScope}) => {
    // React committed a new destination, but the store still has the previous one's scope.
    if (destinationKey !== storeDestinationKey) {
      store.commitNavigationScope(renderedScope)
      storeDestinationKey = destinationKey
    }
  }

  return {
    store,
    get storeDestinationKey() {
      return storeDestinationKey
    },
    applyScope,
  }
}
